import React, { useState, useEffect } from 'react';
import { Form, FormHistory, formService } from '../../../services/formService';
import { useAuth } from '../../../contexts/AuthContext';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { Alert, Button, Form as UiForm, HistoryPanel, ListGroup, Spinner } from '@/components/ui';
import { formatDateTime } from '@/utils/displayDateTime';
import { describeActionError } from '@/utils/loadError';
import { formHistoryEntries } from '../utils/formHistoryEntries';

interface FormHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  form: Form;
  onAddComment: (updatedForm: Form) => void;
}

const FormHistoryModal: React.FC<FormHistoryModalProps> = ({
  visible,
  onClose,
  form,
  onAddComment
}) => {
  const { currentUser } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A failed comment is reported next to the composer (STATE-06), not in a browser alert.
  const [commentError, setCommentError] = useState<string | null>(null);
  const [localHistory, setLocalHistory] = useState<FormHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch history when modal becomes visible
  useEffect(() => {
    const fetchHistory = async () => {
      if (visible) {
        try {
          setIsLoading(true);
          const history = await formService.getFormHistory(form.id);
          setLocalHistory(history);
        } catch (error) {
          console.error('Error fetching form history:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchHistory();
  }, [visible, form.id]);

  const applySoftReload = (refetchedHistory: FormHistory[]) => {
    setLocalHistory(refetchedHistory);
    setNewComment('');
    onAddComment({ ...form, formHistory: refetchedHistory });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const commentText = newComment.trim();
    if (!commentText || !currentUser?.id) return;

    setIsSubmitting(true);
    setCommentError(null);
    const previousLength = localHistory.length;

    try {
      await formService.addHistoryComment(form.id, {
        comments: commentText,
        userId: currentUser.id,
        isExternal: false
      });

      // Soft reload: refetch history and update modal (API returns single entry, not full form)
      const refetchedHistory = await formService.getFormHistory(form.id);
      applySoftReload(refetchedHistory);
    } catch (error) {
      console.error('Error adding comment:', error);
      // Recovery: entry may have been saved but response failed (e.g. serialization)
      try {
        const refetchedHistory = await formService.getFormHistory(form.id);
        const hasNewEntry = refetchedHistory.length > previousLength ||
          refetchedHistory.some((h) => h.comments === commentText && h.stage === 'comment');
        if (hasNewEntry) {
          applySoftReload(refetchedHistory);
          return;
        }
      } catch (_) {
        // Refetch failed; fall through to the inline error
      }
      setCommentError(describeActionError(error, "Couldn't add the comment. Try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  // V2-061: one plain-language entry per event, newest first.
  const entries = formHistoryEntries(localHistory);

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Form History"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <HistoryPanel
        composer={
          <form onSubmit={handleSubmit}>
            {commentError && (
              <Alert variant="danger" role="alert" className="tw-mb-3">
                {commentError}
              </Alert>
            )}
            <div className="tw-mb-3">
              <UiForm.Label>Comment</UiForm.Label>
              <UiForm.Control
                as="textarea"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                // Shorter on a phone, where the composer sits above the timeline and a
                // six-row textarea would push the history off screen on its own.
                rows={3}
                placeholder="Enter your comment here..."
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              className="tw-w-full"
              disabled={!newComment.trim() || isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Comment'}
            </Button>
          </form>
        }
      >
        {isLoading ? (
          <div className="tw-text-center">
            <Spinner className="tw-text-primary">Loading...</Spinner>
          </div>
        ) : entries.length > 0 ? (
          <ListGroup>
            {entries.map((entry) => (
              <ListGroup.Item key={entry.key}>
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-min-w-0">
                    <h6 className="tw-mb-1">{entry.title}</h6>
                    {entry.changes.length > 0 && (
                      <ul className="tw-mb-1 tw-list-none tw-pl-0 tw-text-sm">
                        {entry.changes.map((change, i) => (
                          <li key={i}>{change}</li>
                        ))}
                      </ul>
                    )}
                    {entry.comments && <p className="tw-mb-1">{entry.comments}</p>}
                    <small className="tw-text-content-muted">{entry.by}</small>
                  </div>
                  <small className="tw-shrink-0 tw-text-content-muted tw-ml-2">
                    {formatDateTime(entry.createdAt)}
                  </small>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="info">No history records found.</Alert>
        )}
      </HistoryPanel>
    </ResponsiveModal>
  );
};

export default FormHistoryModal; 